/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useRef, useState } from 'react';
import { BadgeCheck, CheckCircle2, Copy, FileCheck2, Loader2, Package, ShieldCheck, Stamp, Upload, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import api, { describeError } from '@/lib/api';
import { useAsyncAction } from '@/hooks/useAsyncAction';
import { fmt, short, copy, downloadBlob, bytes } from './helpers';

/* ------------------------------------------------------------------ Verifica release */
export const hashFile = async (file, onProgress) => {
  if (window.crypto?.subtle) {
    const buf = await file.arrayBuffer();
    onProgress?.(1);
    const digest = await window.crypto.subtle.digest('SHA-256', buf);
    return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
  }
  throw new Error('Web Crypto non disponibile: apri la pagina in HTTPS');
};

export const ReleaseSealButton = ({ sha256, filename, size = 'sm', onDone }) => {
  const { run, busy } = useAsyncAction();
  const seal = () => run(async () => {
    const r = await api.post(`/legal/release/seal.pdf${sha256 ? `?sha256=${sha256}` : ''}`, null, { responseType: 'blob' });
    const cd = r.headers['content-disposition'] || '';
    const m = /filename="?([^";]+)"?/.exec(cd);
    downloadBlob(r.data, m ? m[1] : `${(filename || 'release').replace(/\.zip$/i, '')}-SIGILLO.pdf`);
    const info = { gen_time: r.headers['x-timestamp-gentime'], serial_number: r.headers['x-timestamp-serial'], signer_cn: r.headers['x-timestamp-tsa'], files: r.headers['x-manifest-files'], seal_sha256: r.headers['x-document-sha256'] };
    onDone?.(info);
    return info;
  }, { success: (i) => `Sigillo release scaricato · marca ${fmt(i.gen_time)} · ${i.files} file nel manifesto` });
  return (
    <Button size={size} variant="outline" className="rounded-xl border-primary/40 text-primary hover:bg-primary/5" onClick={seal} disabled={busy} title="PDF con impronta dello ZIP, marca temporale RFC 3161 e manifesto SHA-256 di ogni file; allegati .tsr, MANIFEST e SHA256SUMS" data-testid={`release-seal-btn-${(sha256 || 'current').slice(0, 8)}`}>
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />} PDF di sigillo release
    </Button>
  );
};

export const ReleaseVerifyCard = () => {
  const [manifest, setManifest] = useState(null);
  const [sealInfo, setSealInfo] = useState(null);
  const [state, setState] = useState({ phase: 'idle' }); // idle | hashing | verifying | done | error
  const [expected, setExpected] = useState('');
  const inputRef = useRef(null);
  const { run: runStamp, busy: stamping } = useAsyncAction();

  useEffect(() => { api.get('/legal/release/manifest').then((r) => setManifest(r.data)).catch(() => setManifest({ entries: [], count: 0 })); }, []);

  const verify = async (file) => {
    if (!file) return;
    setState({ phase: 'hashing', filename: file.name, size: file.size });
    try {
      const sha = await hashFile(file);
      setState((s) => ({ ...s, phase: 'verifying', sha256: sha }));
      const r = await api.post('/legal/release/verify', { sha256: sha, filename: file.name, size: file.size });
      const manual = expected.trim().toLowerCase();
      const manualOk = manual ? manual === sha : null;
      setState({ phase: 'done', filename: file.name, size: file.size, sha256: sha, ok: r.data.ok || manualOk === true, server: r.data, manualOk });
    } catch (e) {
      setState({ phase: 'error', filename: file.name, error: describeError(e, 'Verifica non riuscita') });
    }
  };

  const onDrop = (e) => { e.preventDefault(); verify(e.dataTransfer?.files?.[0]); };
  const stampRelease = () => runStamp(async () => {
    const r = await api.post('/legal/timestamp', { sha256: state.sha256, filename: state.filename, kind: 'release' });
    setState((s) => ({ ...s, server: { ...s.server, timestamp: r.data } }));
    return r.data;
  }, { success: (d) => `Release marcata · ${fmt(d.gen_time)}` });

  const busy = state.phase === 'hashing' || state.phase === 'verifying';
  return (
    <div className="not-prose space-y-3" data-testid="release-verify">
      <h3 className="font-display text-lg font-semibold flex items-center gap-2"><Package className="h-4 w-4 text-primary" /> Verifica release scaricata</h3>
      <p className="text-sm text-muted-foreground">Trascina qui lo ZIP scaricato da Emergent (o da <span className="font-mono">release/</span>): l'impronta SHA-256 viene calcolata <b>nel tuo browser</b> (il file non viene caricato) e confrontata con <span className="font-mono">SHA256SUMS.txt</span> della release pubblicata.</p>
      <Card className="rounded-2xl">
        <CardContent className="p-4 space-y-3">
          <div className="text-xs text-muted-foreground" data-testid="release-known">
            Impronte note: {manifest ? manifest.count : '…'}
            {manifest?.entries?.map((e) => (
              <div key={e.sha256} className="flex flex-wrap items-center gap-2 mt-1">
                <span className="font-mono">{short(e.sha256)} · {e.filename}{e.size ? ` · ${bytes(e.size)}` : ''}</span>
                <ReleaseSealButton sha256={e.sha256} filename={e.filename} onDone={setSealInfo} />
              </div>
            ))}
          </div>
          {sealInfo && (
            <div className="rounded-xl border border-primary/30 bg-primary/5 p-3 text-xs space-y-1" data-testid="release-seal-info">
              <div className="flex items-center gap-2 font-semibold text-primary"><BadgeCheck className="h-4 w-4" /> Sigillo release generato</div>
              <div>Marca temporale: <b>{fmt(sealInfo.gen_time)}</b> · {sealInfo.signer_cn} · serie <span className="font-mono">{sealInfo.serial_number}</span></div>
              <div>Manifesto: {sealInfo.files} file · SHA-256 del PDF di sigillo: <code className="font-mono break-all">{sealInfo.seal_sha256}</code></div>
              <div className="text-muted-foreground">Il PDF contiene in allegato la ricevuta .tsr, MANIFEST.sha256 e SHA256SUMS.txt: conservalo insieme allo ZIP.</div>
            </div>
          )}
          <div
            role="button" tabIndex={0}
            onClick={() => inputRef.current?.click()} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click(); }}
            onDragOver={(e) => e.preventDefault()} onDrop={onDrop}
            className={`rounded-xl border-2 border-dashed p-6 text-center cursor-pointer transition-colors ${busy ? 'opacity-60 pointer-events-none' : 'hover:border-primary hover:bg-primary/5'} border-border`}
            data-testid="release-dropzone"
          >
            <Upload className="h-6 w-6 mx-auto text-primary mb-2" />
            <div className="text-sm font-medium">{busy ? (state.phase === 'hashing' ? 'Calcolo SHA-256 nel browser…' : 'Confronto con la release pubblicata…') : 'Trascina lo ZIP o clicca per selezionarlo'}</div>
            <div className="text-xs text-muted-foreground mt-1">Nessun upload: solo l'impronta viene inviata al server.</div>
            <input ref={inputRef} type="file" accept=".zip,application/zip" className="hidden" onChange={(e) => verify(e.target.files?.[0])} data-testid="release-file-input" />
          </div>
          <div>
            <Label className="text-xs">Impronta attesa (opzionale, da SHA256SUMS.txt o dalla ricevuta di marcatura)</Label>
            <Input value={expected} onChange={(e) => setExpected(e.target.value)} placeholder="e221c810bc3f…" className="h-10 rounded-xl font-mono text-xs" data-testid="release-expected-input" />
          </div>

          {state.phase === 'error' && (
            <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive flex items-center gap-2" data-testid="release-result-error"><XCircle className="h-4 w-4" /> {state.error}</div>
          )}
          {state.phase === 'done' && (
            <div className={`rounded-xl border p-4 space-y-2 ${state.ok ? 'border-primary/40 bg-primary/5' : 'border-destructive/40 bg-destructive/5'}`} data-testid="release-result" data-ok={state.ok ? 'true' : 'false'}>
              <div className={`flex items-center gap-2 font-semibold ${state.ok ? 'text-primary' : 'text-destructive'}`} data-testid="release-result-status">
                {state.ok ? <CheckCircle2 className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
                {state.ok ? 'Impronta coincidente: il file è identico alla release pubblicata' : 'Impronta NON coincidente: il file differisce dalla release pubblicata'}
              </div>
              <div className="text-xs grid gap-1">
                <div><span className="text-muted-foreground w-32 inline-block">File</span><span className="font-mono">{state.filename}</span> · {bytes(state.size)}</div>
                <div className="flex items-center gap-1"><span className="text-muted-foreground w-32 inline-block">SHA-256 calcolato</span><code className="font-mono break-all" data-testid="release-result-sha">{state.sha256}</code><Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => copy(state.sha256, 'SHA-256 copiato')}><Copy className="h-3 w-3" /></Button></div>
                {state.server?.matched && <div><span className="text-muted-foreground w-32 inline-block">Corrisponde a</span><span className="font-mono">{state.server.matched.filename}</span></div>}
                {state.manualOk != null && <div><span className="text-muted-foreground w-32 inline-block">Impronta attesa</span>{state.manualOk ? <span className="text-primary">coincide</span> : <span className="text-destructive">non coincide</span>}</div>}
                <div className="flex items-center gap-2 flex-wrap pt-1">
                  <span className="text-muted-foreground w-32 inline-block">Marca temporale</span>
                  {state.server?.timestamp
                    ? <Badge className="rounded-full bg-primary/10 text-primary hover:bg-primary/10"><BadgeCheck className="h-3 w-3 mr-1" /> {fmt(state.server.timestamp.gen_time)} · {state.server.timestamp.signer_cn || state.server.timestamp.tsa_url}</Badge>
                    : state.ok
                      ? <Button size="sm" variant="outline" className="rounded-xl h-8" onClick={stampRelease} disabled={stamping} data-testid="release-stamp-btn">{stamping ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Stamp className="h-3.5 w-3.5" />} Marca questa release ora</Button>
                      : <span className="text-muted-foreground">non disponibile per file non riconosciuti</span>}
                  {state.ok && <ReleaseSealButton sha256={state.sha256} filename={state.filename} onDone={setSealInfo} />}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      <p className="text-[11px] text-muted-foreground flex items-start gap-1.5"><FileCheck2 className="h-3.5 w-3.5 mt-0.5 shrink-0" /> Ogni verifica viene registrata nell'audit log (<span className="font-mono">release.verify</span>) con esito, così resta traccia di quale archivio hai controllato e marcato.</p>
    </div>
  );
};
